import React, { useEffect, useRef, useState } from "react";
import "./CarbonCredits.css";
import ccHeroImg from "../../assets/CCsection1.png";
import ccThumbImg from "../../assets/CCsection1-thumb.png";
import ccSection2Img from "../../assets/CCsection2.png";
import ccSection4Img from "../../assets/CCsection4.png";
import carbonFinancial1 from "../../assets/carbon financial-1.png";
import carbonFinancial2 from "../../assets/carbon financial-2.png";
import carbonFinancial3 from "../../assets/carbon financial-3.png";
import carbonFinancial4 from "../../assets/carbon financial-4.png";
import carbonFinancial5 from "../../assets/carbon financial-5.png";
// Add with other imports at top
import ccIcon1 from "../../assets/CCicon1.png";
import ccIcon2 from "../../assets/CCicon2.png";
import ccIcon3 from "../../assets/CCicon3.png";
import ccIcon4 from "../../assets/CCicon4.png";
import ccIcon5 from "../../assets/CCicon5.png";
import trueRenewablesLogo from "../../assets/hiw_logo.svg";
import finalStep1 from "../../assets/finalstep1.png";
import finalStep2 from "../../assets/finalstep2.png";
import finalStep3 from "../../assets/finalstep3.png";
import ccWhyImg from "../../assets/CCsection7.png";
// Add at top with other imports
import ccSection8Img from "../../assets/CCsection8.png";
/* ================================================
   SECTION 1 — HERO
   ================================================ */
const CarbonCreditsHero = () => (
  <section className="cc-hero">
    <div className="cc-hero-left">
      <h1 className="cc-hero-title">The New Era Of Climate Accountability</h1>

      <div className="cc-hero-thumb-wrap">
        <img src={ccThumbImg} alt="Carbon Credits" className="cc-hero-thumb" />
      </div>

      <p className="cc-hero-desc">
        True Renewables provides a complete carbon credit ecosystem for
        industries, investors, and corporate sustainability teams looking to
        unlock verified environmental attributes from their solar projects, both
        captive and open access.
      </p>

      <button className="cc-hero-cta">Book A Free Call</button>
    </div>

    <div className="cc-hero-right">
      <img src={ccHeroImg} alt="Carbon Credits Solar" className="cc-hero-img" loading="lazy" />
    </div>
  </section>
);

/* ================================================
   SECTION 2 — WHAT IS A CARBON CREDIT
   ================================================ */
const CarbonCreditsExplainer = () => {
  const checkItems = [
    "Registered",
    "Verified",
    "Certified",
    "Traded",
    "Retired for ESG reporting",
  ];

  return (
    <section className="cc-explainer">
      <div className="cc-explainer-inner">
        {/* ── Left card ── */}
        <div className="cc-explainer-card">
          <div className="cc-explainer-card-top">
            <p className="cc-explainer-heading">
              A carbon credit is a verified certificate representing 1 ton of CO
              <sub>2</sub> equivalent reduced or avoided.
            </p>

            <p className="cc-explainer-body">
              When your business generates clean energy through solar assets,
              you create measurable and verifiable carbon reductions. These
              reductions can be:
            </p>

            <ul className="cc-explainer-list">
              {checkItems.map((item, i) => (
                <li key={i} className="cc-explainer-list-item">
                  <svg
                    className="cc-explainer-check"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 12.5L9.5 18L20 7"
                      stroke="#C04820"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="cc-explainer-tagline">
            True Renewables ensures these environmental attributes become{" "}
            <strong>real financial and sustainability assets</strong> for your
            company.
          </p>
        </div>

        {/* ── Right image ── */}
        <div className="cc-explainer-img-wrap">
          <img
            src={ccSection2Img}
            alt="Carbon Credit"
            className="cc-explainer-img"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 3 — STATS
   ================================================ */
const ccStatsData = [
  {
    id: 1,
    label: "MW Solar\nCapacity Installed",
    value: 120,
    suffix: "+",
    color: "blue",
  },
  {
    id: 2,
    label: "Tonnes of CO₂\nEmissions Reduced",
    value: 25,
    suffix: "+",
    color: "orange",
  },
  {
    id: 3,
    label: "Successful\nProjects Delivered",
    value: 99,
    suffix: "%",
    color: "blue",
  },
  {
    id: 4,
    label: "Years of Industry\nExperience",
    value: 10,
    suffix: "+",
    color: "orange",
  },
];

const CarbonCreditsStats = () => {
  const sectionRef = useRef(null);
  const animatingRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [counts, setCounts] = useState(ccStatsData.map(() => 0));

  /* ── trigger visibility on scroll ── */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const check = () => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.65) {
        setCounts(ccStatsData.map(() => 0));
        animatingRef.current = false;
        setVisible(true);
        window.removeEventListener("scroll", check);
      }
    };

    const timer = setTimeout(() => {
      window.addEventListener("scroll", check, { passive: true });
      check();
    }, 400);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", check);
    };
  }, []);

  /* ── count-up animation ── */
  useEffect(() => {
    if (!visible || animatingRef.current) return;
    animatingRef.current = true;

    ccStatsData.forEach((stat, index) => {
      let current = 0;
      const stepTime = 20;
      const increment = stat.value / (1500 / stepTime);

      const timer = setInterval(() => {
        current += increment;
        if (current >= stat.value) {
          current = stat.value;
          clearInterval(timer);
        }
        setCounts((prev) => {
          const copy = [...prev];
          copy[index] = Math.floor(current);
          return copy;
        });
      }, stepTime);
    });
  }, [visible]);

  return (
    <section className="cc-stats" ref={sectionRef}>
      <div className="cc-stats-inner">
        {ccStatsData.map((stat, index) => (
          <div key={stat.id} className="cc-stat-item">
            <p className="cc-stat-label">{stat.label}</p>
            <h3 className={`cc-stat-number cc-stat-${stat.color}`}>
              {counts[index]}
              {stat.suffix}
            </h3>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ================================================
   SECTION 4 — GLOBAL CERTIFICATES
   ================================================ */
const carbonCertItems = [
  {
    title: "RECs (Renewable Energy Certificates — India)",
    desc: "Issued as per CERC regulations for grid-connected renewable power.",
    image: carbonFinancial1,
  },
  {
    title: "I-RECs (International Renewable Energy Certificates)",
    desc: "Issued as per CERC regulations for grid-connected renewable power.",
    image: carbonFinancial2,
  },
  {
    title: "VERs (Verified Emission Reductions – Voluntary Market)",
    desc: "Issued as per CERC regulations for grid-connected renewable power.",
    image: carbonFinancial3,
  },
  {
    title: "Corporate Sustainability Attributes",
    desc: "Issued as per CERC regulations for grid-connected renewable power.",
    image: carbonFinancial4,
  },
  {
    title: "Green Attribute Monetisation",
    desc: "Issued as per CERC regulations for grid-connected renewable power.",
    image: carbonFinancial5,
  },
];

const CarbonCertificates = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const outerRef = useRef(null);
  const activeIndexRef = useRef(0);

  /* ── Pin the panel while scrolling through the outer (tall) wrapper,
     driving activeIndex from scroll progress. ── */
  useEffect(() => {
    let raf;

    const update = () => {
      const el = outerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const totalScroll = el.offsetHeight - window.innerHeight;
        const scrolled = -rect.top;
        const p =
          totalScroll > 0
            ? Math.max(0, Math.min(scrolled / totalScroll, 1))
            : 0;

        const idx = Math.min(
          carbonCertItems.length - 1,
          Math.floor(p * carbonCertItems.length)
        );

        if (idx !== activeIndexRef.current) {
          activeIndexRef.current = idx;
          setActiveIndex(idx);
        }
      }

      raf = requestAnimationFrame(update);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    const timer = setTimeout(() => update(), 100);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="cc-certs-outer"
      ref={outerRef}
      style={{ height: `${carbonCertItems.length * 110}vh` }}
    >
      <section className="cc-certs-sticky">
        <div className="cc-certs">
          <div className="cc-certs-header">
            <p className="cc-certs-tag">
              <span className="cc-certs-dot" />
              ENVIRONMENTAL CERTIFICATE SOLUTIONS
            </p>
            <h2 className="cc-certs-title">
              Global Renewable Energy &amp; Carbon Certificates
            </h2>
          </div>

          <div className="cc-certs-body">
            <div className="cc-certs-list">
              {carbonCertItems.map((item, i) => (
                <div key={i}>
                  <div
                    className={`cc-cert-item ${
                      i === activeIndex ? "cc-cert-item--active" : ""
                    }`}
                    onClick={() => setActiveIndex(i)}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    {/* Orange bar — only for active */}
                    {i === activeIndex && <span className="cc-cert-bar" />}

                    <div className="cc-cert-text">
                      <p className="cc-cert-title">{item.title}</p>

                      {/* Desc — only for active, animates in */}
                      {i === activeIndex && (
                        <p className="cc-cert-desc">{item.desc}</p>
                      )}
                    </div>
                  </div>

                  {/* Mobile-only inline image — appears below the active item */}
                  <div className="cc-cert-mobile-img-wrap">
                    <div className="cc-cert-mobile-img-bg" />
                    <img
                      src={item.image}
                      alt={item.title}
                      className="cc-cert-mobile-img"
                      loading="lazy"
                    />
                  </div>

                  {i < carbonCertItems.length - 1 && (
                    <hr className="cc-cert-divider" />
                  )}
                </div>
              ))}
            </div>

            <div className="cc-certs-img-wrap">
              <div className="cc-certs-img-bg" />
              <img
                src={carbonCertItems[activeIndex].image}
                alt={carbonCertItems[activeIndex].title}
                className="cc-certs-img"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ================================================
   SECTION 5 — WHY CARBON CREDITS MATTER
   ================================================ */
const CarbonValueItems = () => {
  const items = [
    {
      icon: ccIcon1,
      title: "ESG & Sustainability Compliance",
      desc: "Meet corporate sustainability commitments and international reporting standards.",
    },
    {
      icon: ccIcon2,
      title: "Scope 2 Emissions Reduction",
      desc: "Officially reduce grid electricity emissions using verified renewable energy credits.",
    },
    {
      icon: ccIcon3,
      title: "Monetisation (Revenue Stream)",
      desc: "Sell carbon credits in domestic or international markets.",
    },
    {
      icon: ccIcon4,
      title: "Brand Leadership & Green Credentials",
      desc: "A powerful signal to investors, clients, and global partners.",
    },
    {
      icon: ccIcon5,
      title: "Alignment With Global Standards",
      desc: "CDP • GRI • SBTi • ISO 14064 • Corporate Sustainability Reporting Directive (CSRD)",
    },
  ];

  return (
    <section className="cc-value">
      <div className="cc-value-inner">
        {/* ── Left sticky col ── */}
        <div className="cc-value-left">
          <p className="cc-value-tag">
            <span className="cc-value-dot" />
            CARBON VALUE FOR BUSINESS
          </p>
          <h2 className="cc-value-title">
            Why Carbon Credits Matter For Modern Enterprises
          </h2>
        </div>

        {/* ── Right scrolling col ── */}
        <div className="cc-value-right">
          {items.map((item, i) => (
            <div key={i}>
              <div className="cc-value-row">
                <img
                  src={item.icon}
                  alt={item.title}
                  className="cc-value-icon"
                  loading="lazy"
                />
                <div className="cc-value-text">
                  <p className="cc-value-item-title">{item.title}</p>
                  <p className="cc-value-item-desc">{item.desc}</p>
                </div>
              </div>
              {i < items.length - 1 && <hr className="cc-value-divider" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 6 — HOW IT WORKS
   ================================================ */
const ccStages = [
  {
    id: 1,
    title: "Baseline & Feasibility",
    steps: [
      { title: "Assessment of plant type (OA, Captive, GC, C&I Rooftop, Utility-scale)" },
      { title: "Calculation of baseline grid emission factors" },
      { title: "Eligibility check for REC / I-REC / VER (Verra / Gold Standard)" },
      { title: "Identification of emission avoidance methodology" },
      { title: "Estimation of annual CO₂ reduction potential" },
      { title: "Risk mapping: double counting, ownership rights, registry restrictions" },
    ],
  },
  {
    id: 2,
    title: "Registry & Strategy",
    steps: [
      {
        title: "Selection of appropriate carbon instrument:",
        bullets: ["REC (India-specific renewable attribute)", "I-REC (International market)", "VER (Voluntary carbon credits: Verra, Gold Standard)"],
      },
      {
        title: "Strategic planning:",
        bullets: ["Monetization vs Retire for ESG reporting", "Long-term issuance timeline planning"],
      },
      { title: "Compliance preparation for chosen registry" },
      { title: "Documentation strategy and filing plan" },
    ],
  },
  {
    id: 3,
    title: "Document Prep",
    steps: [
      { title: "Development of Project Design Document (PDD)" },
      { title: "Mapping project to approved methodology (e.g., ACM0002, AM0018, GS4GG)" },
      { title: "Gathering SCADA & historical data for baselining" },
      { title: "Ownership declarations & legal documentation" },
      { title: "Ensuring data meets registry audit standards" },
      { title: "Stakeholder consultation documentation (if VER)" },
    ],
  },
  {
    id: 4,
    title: "Monitoring Architecture",
    steps: [
      { title: "Defining monitoring parameters (generation, export, losses, downtime)" },
      { title: "Establishing data collection architecture via TrueCare™" },
      { title: "Preparing audit trail structure for verifiers" },
      { title: "Designing sampling & metering methodology" },
      { title: "Building archive structure for 7–10 years of audits" },
      { title: "Setting verification timelines & issuance cycles" },
    ],
  },
  {
    id: 5,
    title: "Commissioning Validation",
    steps: [
      { title: "Validation that EPC execution meets registry technical requirements" },
      { title: "Commissioning date documentation for credit start" },
      { title: "Calibration & sealing of meters" },
      { title: "Verification of grid interconnection" },
      { title: "Final as-built documentation stored in TrueTrack™" },
      { title: "Compliance alignment for REC / I-REC / VER issuance" },
    ],
  },
  {
    id: 6,
    title: "Data Integration",
    steps: [
      { title: "Integration of SCADA & inverter telemetry" },
      { title: "Real-time energy generation & export data capture" },
      { title: "Dual dashboards: engineering & ESG-friendly views" },
      { title: "Data cleaning, validation & anomaly detection" },
      { title: "Establishing precise timestamped performance logs" },
      { title: "Automated data pipelines for verification" },
    ],
  },
  {
    id: 7,
    title: "Performance Monitoring",
    steps: [
      { title: "Daily generation tracking against baseline" },
      { title: "Automated emission reduction computation" },
      { title: "AI alerts for anomalies affecting credit eligibility" },
      { title: "Weather-normalized CUF/PR reporting" },
      { title: "Predictive modelling for carbon output forecasts" },
      { title: "Export-ready performance datasets for auditors" },
    ],
  },
  {
    id: 8,
    title: "Verification Prep",
    steps: [
      { title: "Compilation of monitoring reports" },
      { title: "Evidence bundles: testing reports, meter readings, SCADA logs" },
      { title: "Support during third-party verification audits" },
      { title: "Corrective action responses (if required)" },
      { title: "Preparing registry submission package" },
      { title: "Ensuring full traceability for every emission claim" },
    ],
  },
  {
    id: 9,
    title: "Credit Issuance",
    steps: [
      { title: "Filing issuance request with chosen registry" },
      { title: "Managing credit issuance into entity account" },
      { title: "Certificate tagging, traceability & ownership marking" },
      { title: "Mapping credits to batches/vintages" },
      { title: "Document archiving for future audits" },
      { title: "Auto-notifications for issuance cycles" },
    ],
  },
  {
    id: 10,
    title: "Trading & Monetization",
    steps: [
      { title: "TrueCom™ Carbon Wallet overview" },
      { title: "Hold / sell / retire credit options" },
      { title: "Market price insights (voluntary + compliance markets)" },
      { title: "Trading strategy advisory (spot vs forward)" },
      { title: "Multiconsumer credit allocation support" },
      { title: "Settlement management & documentation" },
    ],
  },
  {
    id: 11,
    title: "ESG & Reporting",
    steps: [
      { title: "Automated Scope 2 reduction calculation" },
      { title: "ESG dashboards for CDP, GRI, SBTi reporting" },
      { title: "Annual climate impact statements" },
      { title: "Renewable attribute tracking for RE100 compliance" },
      { title: "Corporate sustainability report integration" },
      { title: "Carbon footprint reduction certification support" },
    ],
  },
  {
    id: 12,
    title: "Lifecycle Optimization",
    steps: [
      { title: "Multi-year credit forecast planning" },
      { title: "Portfolio-level carbon asset management across sites" },
      { title: "Annual performance audits & optimization suggestions" },
      { title: "Risk analysis: regulation, pricing, additionality rules" },
      { title: "Expansion planning for new carbon-generating assets" },
      { title: "Maintaining compliance for 7–10 year crediting cycles" },
    ],
  },
];

const CC_N = ccStages.length;
const CC_RADIUS = 380;
const CC_ANGLE_STEP = 30;
const CC_MINOR_TICKS = 72;

const CarbonHowItWorks = () => {
  const sectionRef = useRef(null);
  const dialRef = useRef(null);
  const numRefs = useRef([]);
  const tickRefs = useRef([]);
  const itemRefs = useRef([]);
  const cardsContainerRef = useRef(null);
  const arcRef = useRef(null);
  const activeStageRef = useRef(0);
  const logoRef = useRef(null);

  const [activeIdx, setActiveIdx] = useState(0);
  const [isMobilePopupOpen, setIsMobilePopupOpen] = useState(false);

  useEffect(() => {
    let raf;
    const update = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const headerEl = el.querySelector(".cc-hiw-header-wrap");
      const headerHeight = headerEl ? headerEl.offsetHeight : 0;
      const startDelay = headerHeight + window.innerHeight * 0.2;
      const totalScroll = el.offsetHeight - window.innerHeight - startDelay;
      const scrolled = -rect.top - startDelay;
      const p = Math.max(0, Math.min(scrolled / totalScroll, 1));
      const arcFloat = p * CC_N;
      const activeFloat = Math.min(arcFloat, CC_N - 1);
      const activeIndex = Math.round(activeFloat);
      const currentRotation = -(activeFloat * CC_ANGLE_STEP);
      const mobileOffset = window.innerWidth <= 1024 ? -90 : 0;

      if (dialRef.current) {
        dialRef.current.style.transform = `translate(-50%, -50%) rotate(${currentRotation + mobileOffset}deg)`;
      }
      if (logoRef.current) {
        const logoOffset = window.innerWidth <= 1024 ? 90 : 0;
        logoRef.current.style.transform = `rotate(${currentRotation + logoOffset}deg)`;
      }

      ccStages.forEach((_, i) => {
        const numEl = numRefs.current[i];
        const tickEl = tickRefs.current[i];
        const itemEl = itemRefs.current[i];
        if (!numEl || !tickEl || !itemEl) return;
        const dist = activeFloat - i;
        let opacity = 1;
        if (Math.abs(dist) < 0.5) opacity = 0;
        else if (dist > 0.5) opacity = Math.max(0, 1 - (dist - 0.5) * 0.4);
        else if (dist < -5) opacity = 0;
        numEl.style.opacity = opacity;
        tickEl.style.opacity = 1;
        itemEl.style.opacity = opacity;
        numEl.style.transform = `rotate(${-(i * CC_ANGLE_STEP) - currentRotation - mobileOffset}deg)`;
      });

      if (arcRef.current) {
        const progress = Math.max(0, Math.min(arcFloat, CC_N)) / 12;
        const circ = 2 * Math.PI * 333;
        arcRef.current.style.strokeDashoffset = circ - progress * circ;
      }

      if (activeIndex !== activeStageRef.current) {
        activeStageRef.current = activeIndex;
        setActiveIdx(activeIndex);
      }

      raf = requestAnimationFrame(update);
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    setTimeout(() => update(), 100);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const currentStage = ccStages[activeIdx];
  const numStr = (activeIdx + 1).toString().padStart(2, "0");
  const cardsData = currentStage.steps;
  const stepImages = [finalStep1, finalStep2, finalStep3];

  const scrollCards = (direction) => {
    if (cardsContainerRef.current) {
      const scrollAmount = cardsContainerRef.current.offsetWidth;
      cardsContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="cc-hiw-outer" ref={sectionRef} style={{ height: `${ccStages.length * 110}vh` }}>
      <div className="cc-hiw-header-wrap">
        <div className="cc-hiw-tag"><span className="cc-hiw-dot"></span>HOW IT WORKS</div>
        <h2 className="cc-hiw-title">True 360° Carbon Credit Ecosystem</h2>
        <p className="cc-hiw-subtitle">
          A fully managed, end-to-end carbon credit lifecycle — automated, transparent, compliant.
        </p>
      </div>

      <div className="cc-hiw-sticky">
        <div className="cc-hiw-split">
          <div className="cc-hiw-left">
            <div className="cc-hiw-mobile-header">
              <div className="cc-hiw-tag"><span className="cc-hiw-dot"></span>HOW IT WORKS</div>
              <h2 className="cc-hiw-title">True 360° Carbon Credit Ecosystem</h2>
              <p className="cc-hiw-subtitle">A fully managed, end-to-end carbon credit lifecycle — automated, transparent, compliant.</p>
            </div>

            <div className="cc-hiw-dial-wrapper">
              <div className="cc-hiw-dial-container" ref={dialRef}>
                <div className="cc-hiw-dial-bg"></div>
                <div className="cc-hiw-dial-inner-orange"></div>
                {Array.from({ length: CC_MINOR_TICKS }).map((_, i) => (
                  <div key={`tick-${i}`} className="cc-hiw-dial-tick-min"
                    style={{ transform: `rotate(${i * (360 / CC_MINOR_TICKS)}deg) translate(${CC_RADIUS}px)` }} />
                ))}
                <svg style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"678px", height:"678px", pointerEvents:"none", zIndex:2 }}>
                  <circle ref={arcRef} cx="339" cy="339" r="333" fill="none" stroke="#F05A28" strokeWidth="6"
                    strokeDasharray={2 * Math.PI * 333} strokeDashoffset={2 * Math.PI * 333} strokeLinecap="round" />
                </svg>
                {ccStages.map((stage, i) => {
                  const itemAngle = i * CC_ANGLE_STEP;
                  const isActive = i === activeIdx;
                  const isPast = i < activeIdx;
                  return (
                    <React.Fragment key={stage.id}>
                      <div className={`cc-hiw-dial-tick-maj ${isActive ? "active" : ""} ${isPast ? "past" : ""}`}
                        ref={(el) => (tickRefs.current[i] = el)}
                        style={{ transform: `rotate(${itemAngle}deg) translate(${CC_RADIUS}px)` }} />
                      <div className={`cc-hiw-dial-item ${isPast ? "past" : ""}`}
                        ref={(el) => (itemRefs.current[i] = el)}
                        style={{ transform: `rotate(${itemAngle}deg) translate(${CC_RADIUS + 40}px)` }}>
                        <div className="cc-hiw-dial-num" ref={(el) => (numRefs.current[i] = el)} style={{ transform: "rotate(-90deg)" }}>
                          {(i + 1).toString().padStart(2, "0")}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="cc-hiw-dial-inner">
                <img src={trueRenewablesLogo} alt="True Renewables" className="cc-hiw-center-logo" ref={logoRef} loading="lazy" />
                <div className="cc-hiw-dial-pointer"></div>
              </div>
              <div className="cc-hiw-active-overlay">
                <div className="cc-hiw-inner-faint-line"></div>
                <div className="cc-hiw-active-thick-line"></div>
                <div className="cc-hiw-active-dot"></div>
                <div className="cc-hiw-active-text-block">
                  <span className="cc-hiw-active-num">{numStr}</span>
                  <span className="cc-hiw-active-title">{currentStage.title}</span>
                </div>
              </div>
            </div>

            <div className="cc-hiw-mobile-info">
              <div className="cc-hiw-mobile-stage">STAGE {activeIdx + 1}</div>
              <h3 className="cc-hiw-mobile-title">{currentStage.title}</h3>
              <p className="cc-hiw-mobile-desc">{currentStage.steps.map((s) => s.title).join(", ")}</p>
              <button className="cc-hiw-mobile-view-btn" onClick={() => setIsMobilePopupOpen(true)}>VIEW DETAILS ↗</button>
            </div>
          </div>

          <div className={`cc-hiw-right ${isMobilePopupOpen ? "mobile-open" : ""}`}>
            <div className="cc-hiw-popup-bg" onClick={() => setIsMobilePopupOpen(false)}></div>
            <div className="cc-hiw-popup-inner">
              <button className="cc-hiw-popup-close" onClick={() => setIsMobilePopupOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <div className="cc-hiw-stage-badge">STAGE {numStr}</div>
              <h2 className="cc-hiw-right-title">{currentStage.title}</h2>
              <div className="cc-hiw-cards-container" ref={cardsContainerRef}>
                {cardsData.map((step, idx) => (
                  <div key={idx} className="cc-hiw-step-card">
                    <div className="cc-hiw-step-img-wrap">
                      <img src={stepImages[Math.min(idx, 2)]} alt={step.title} className="cc-hiw-step-img" loading="lazy" />
                    </div>
                    <div className="cc-hiw-step-text">
                      <p className="cc-hiw-step-num">STEP {idx + 1}</p>
                      <h4 className="cc-hiw-step-title">{step.title}</h4>
                      {step.bullets && (
                        <ul className="cc-hiw-step-bullets">
                          {step.bullets.map((bullet, i) => (
                            <li key={i} className="cc-hiw-step-bullet">{bullet}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="cc-hiw-nav-arrows">
                <button type="button" className="cc-hiw-nav-arrow" onClick={() => scrollCards("left")}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <button type="button" className="cc-hiw-nav-arrow" onClick={() => scrollCards("right")}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>
              <div className="cc-hiw-popup-mobile-info">
                <div className="cc-hiw-mobile-stage">STAGE {activeIdx + 1}</div>
                <h3 className="cc-hiw-mobile-title">{currentStage.title}</h3>
                <p className="cc-hiw-mobile-desc">{currentStage.steps.map((s) => s.title).join(", ")}</p>
                <button className="cc-hiw-mobile-view-btn popup-btn" onClick={() => setIsMobilePopupOpen(false)}>VIEW DETAILS ↗</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================
   SECTION 7 — WHY CHOOSE US
   ================================================ */
const CarbonWhyChooseUs = () => {
  const leftCards = [
    { num: "01", text: "Integrated digital carbon ecosystem." },
    {
      num: "02",
      text: "Real-time data from TrueCare™ ensures highest accuracy.",
    },
    {
      num: "03",
      text: "Expertise across I-REC, REC, VER, Gold Standard & Voluntary Market.",
    },
  ];
  const rightCards = [
    {
      num: "04",
      text: "Ability to serve global corporations & multi-national ESG portfolios.",
    },
    {
      num: "05",
      text: "Registry-aligned project documentation & lifecycle management.",
    },
    {
      num: "06",
      text: "Strong relationships with accredited auditors & carbon partner.",
    },
  ];

  return (
    <section className="cc-why">
      <div className="cc-why-header">
        <p className="cc-why-tag">
          <span className="cc-why-dot" />
          WHY CHOOSE US
        </p>
        <h2 className="cc-why-title">
          Carbon Expertise.
          <br />
          Global Compliance.
        </h2>
      </div>

      <div className="cc-why-body">
        <div className="cc-why-col">
          {leftCards.map((c) => (
            <div className="cc-why-card" key={c.num}>
              <p className="cc-why-num">{c.num}</p>
              <p className="cc-why-text">{c.text}</p>
            </div>
          ))}
        </div>

        <div className="cc-why-img-wrap">
          <img src={ccWhyImg} alt="Why Choose Us" className="cc-why-img" loading="lazy" />
        </div>

        <div className="cc-why-col">
          {rightCards.map((c) => (
            <div className="cc-why-card" key={c.num}>
              <p className="cc-why-num">{c.num}</p>
              <p className="cc-why-text">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================
   SECTION 8 — THE TRUE ENERGY ADVANTAGE
   ================================================ */
const CarbonEnergyAdvantage = () => {
  const checkItems = [
    "Energy Savings",
    "Long-term Tariff Stability",
    "Carbon Credit Monetisation",
  ];

  return (
    <section className="cc-advantage">
      <div className="cc-advantage-inner">
        {/* ── Left content ── */}
        <div className="cc-advantage-left">
          <div className="cc-advantage-header">
            <p className="cc-advantage-tag">
              <span className="cc-advantage-dot" />
              THE TRUE ENERGY ADVANTAGE
            </p>
            <h2 className="cc-advantage-title">
              One Solar Plant. Multiple Value Streams.
            </h2>
          </div>

          <ul className="cc-advantage-list">
            {checkItems.map((item, i) => (
              <li key={i} className="cc-advantage-list-item">
                <svg
                  className="cc-advantage-check"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 12.5L9.5 18L20 7"
                    stroke="#C04820"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Right image ── */}
        <div className="cc-advantage-img-wrap">
          <div className="cc-advantage-img-bg" />
          <img
            src={ccSection8Img}
            alt="Solar Plant"
            className="cc-advantage-img"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

/* ================================================
   CARBON CREDITS PAGE
   ================================================ */
const CarbonCredits = () => {
  useEffect(() => {
    document.body.classList.add("carbon-page");
    return () => {
      document.body.classList.remove("carbon-page");
    };
  }, []);

  return (
    <>
      <CarbonCreditsHero />
      <CarbonCreditsExplainer />
      <CarbonCreditsStats />
      <CarbonCertificates />
      <CarbonValueItems />
      <CarbonHowItWorks />
      <CarbonWhyChooseUs />
      <CarbonEnergyAdvantage />
    </>
  );
};

export default CarbonCredits;
